import React from "react";
import { Activity, BarChart3, ChevronRight, RefreshCw, TrendingUp } from "lucide-react";

import { getLatestGlobalIndex, getLiveInstrumentIntraday, getMarketIndustries, getMarketIndustryFlow, getMarketOverview, getMarketRealtimeStatus, getMarketSnapshot } from "../../api/client";
import { Chart } from "../../components/charts";
import { HuijinEtfCard } from "./HuijinEtfCard";
import { IndustryActivityPage } from "./IndustryActivity";
import { IndustryDetailPage } from "./IndustryDetailPage";
import { IndustryTablePage } from "./IndustryTablePage";
import { InstrumentDetailPage } from "./InstrumentDetailPage";
import { IndexCard } from "./MarketAtoms";
import { RankPage } from "./RankPage";
import { ErrorState, EvidenceFooter, LoadingState, PageHeading, SectionTitle, Unavailable } from "./MarketScaffold";
import { barOption, distributionOption, lineOption } from "./chartOptions";
import { integer, number, shortDate, signed, toneClass } from "./marketFormat";
import { useLatestRequest } from "./useLatestRequest";
import baseStyles from "./marketObservation.module.css";
import overviewStyles from "./marketOverview.module.css";
import tableStyles from "./marketTables.module.css";

const styles = { ...baseStyles, ...overviewStyles, ...tableStyles };

function MarketOverviewLoading() {
  return (
    <div className={styles.page} aria-busy="true">
      <PageHeading
        eyebrow="MARKET OVERVIEW"
        title="大盘概览"
        description="正在聚合本地真实市场数据；页面结构已就绪，数据将分区显示。"
        status="partial"
      />
      <section className={styles.section}>
        <SectionTitle icon={<TrendingUp size={17} />} title="主要指数" meta="正在读取最新交易日" />
        <div className={styles.indexGrid}>
          {Array.from({ length: 6 }, (_, index) => (
            <article className={`${styles.card} ${styles.loadingCard}`} key={index}>
              <span className={styles.skeletonLabel} />
              <span className={styles.skeletonValue} />
              <span className={styles.skeletonMeta} />
            </article>
          ))}
        </div>
      </section>
      <div className={styles.overviewLoadingNotice} role="status">
        <RefreshCw className={styles.spin} size={18} />
        <div>
          <strong>正在汇总真实市场数据</strong>
          <span>首次读取会计算市场宽度、行业表现与活跃度，不使用演示行情。</span>
        </div>
      </div>
    </div>
  );
}

export function MarketObservationApp({ route, navigate }) {
  const [snapshot, setSnapshot] = React.useState(null);
  const [snapshotError, setSnapshotError] = React.useState("");
  const ignorePageStatus = React.useCallback(() => {}, []);
  const loadSnapshot = React.useCallback(async () => {
    const response = await getMarketSnapshot();
    setSnapshot(response.data);
    setSnapshotError("");
    return response.data;
  }, []);
  React.useEffect(() => {
    void loadSnapshot().catch((error) => {
      setSnapshotError(error.message);
      setSnapshot(null);
    });
  }, [loadSnapshot]);
  if (!snapshot && !snapshotError) return <LoadingState label="正在固定市场快照" detail="页面将使用同一已发布快照加载全部事实。" />;
  if (!snapshot) return <ErrorState error={snapshotError} onRetry={loadSnapshot} />;
  let page;
  if (route.screen === "market_overview") {
    page = <MarketOverview navigate={navigate} onStatus={ignorePageStatus} onRefreshSnapshot={loadSnapshot} snapshotId={snapshot.snapshot_id} completeness={snapshot.completeness} />;
  } else if (route.screen === "market_rank") {
    page = <RankPage navigate={navigate} onStatus={ignorePageStatus} snapshotId={snapshot.snapshot_id} />;
  } else if (route.screen === "industry_flow") {
    page = <IndustryActivityPage navigate={navigate} onStatus={ignorePageStatus} snapshotId={snapshot.snapshot_id} />;
  } else if (route.screen === "industry_detail") {
    page = <IndustryDetailPage industryCode={route.industryCode} navigate={navigate} onStatus={ignorePageStatus} snapshotId={snapshot.snapshot_id} />;
  } else if (route.screen === "instrument_detail") {
    page = <InstrumentDetailPage instrumentCode={route.instrumentCode} navigate={navigate} onStatus={ignorePageStatus} snapshotId={snapshot.snapshot_id} />;
  } else {
    page = <IndustryTablePage mode="performance" navigate={navigate} onStatus={ignorePageStatus} snapshotId={snapshot.snapshot_id} />;
  }
  return (
    <main className={styles.shell} id="main-content" tabIndex="-1">
      {page}
    </main>
  );
}

function MarketOverview({ navigate, onStatus, onRefreshSnapshot, snapshotId, completeness }) {
  const [payload, setPayload] = React.useState(null);
  const [error, setError] = React.useState("");
  const [refreshing, setRefreshing] = React.useState(false);
  const [liveDomesticIndices, setLiveDomesticIndices] = React.useState({});
  const [latestGlobalIndices, setLatestGlobalIndices] = React.useState({});
  const [lastRefreshed, setLastRefreshed] = React.useState(null);
  const requestGuard = useLatestRequest();

  const load = React.useCallback(async (requestedSnapshotId = snapshotId) => {
    const requestVersion = requestGuard.start();
    setRefreshing(true);
    setError("");
    try {
      const data = await getMarketOverview(null, requestedSnapshotId);
      if (!requestGuard.isCurrent(requestVersion)) return;
      setPayload(data);
      setLastRefreshed(new Date());
      onStatus({ status: data.status, dataDate: data.data_date });
    } catch (requestError) {
      if (!requestGuard.isCurrent(requestVersion)) return;
      setError(requestError.message);
      onStatus({ status: "unavailable", dataDate: null });
    } finally {
      if (requestGuard.isCurrent(requestVersion)) setRefreshing(false);
    }
  }, [onStatus, requestGuard, snapshotId]);

  React.useEffect(() => { void load(); }, [load]);

  const refreshMarket = React.useCallback(async () => {
    setRefreshing(true);
    setError("");
    try {
      const latestSnapshot = await onRefreshSnapshot();
      // A newly published snapshot changes snapshotId and lets the normal
      // effect reload every snapshot-pinned fact. If publication has not
      // advanced, still refresh the live overlays and current overview.
      if (latestSnapshot.snapshot_id === snapshotId) await load(snapshotId);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setRefreshing(false);
    }
  }, [load, onRefreshSnapshot, snapshotId]);

  React.useEffect(() => {
    if (!payload?.domestic_indices?.length) return undefined;
    let active = true;
    // Overseas markets have no live pass-through vendor wired up (only the
    // A-share domestic codes use Sina/Tencent), so only these get overlaid;
    // 海外市场 stays on the last published EOD close.
    //
    // "实时" only means something while the market is actually trading. Outside a
    // session the vendor simply replays that day's close, so once the published
    // snapshot caught up to the same day the baseline below (the snapshot's own
    // close) was the very number being compared -- every card read 0.00%. That
    // stayed hidden for as long as the snapshot lagged a day behind, and only
    // surfaced the first evening it did not.
    //
    // Gate on the backend's session state rather than comparing dates: on a
    // weekend "quote date > snapshot date" still holds and would overlay again.
    getMarketRealtimeStatus()
      .then((status) => {
        if (!active) return;
        const session = status?.data?.session_state;
        if (session !== "live_session" && session !== "lunch_break") {
          // Market shut: the published pct_chg already *is* that day's change,
          // so keep it and only relabel. Overlaying here can add nothing except
          // the chance to get it wrong.
          setLiveDomesticIndices(
            Object.fromEntries(
              payload.domestic_indices.map((item) => [item.code, { trade_date: "收盘" }]),
            ),
          );
          return;
        }
        return Promise.all(
          payload.domestic_indices.map((item) =>
            getLiveInstrumentIntraday({ code: item.code })
              .then((live) => [item.code, live])
              .catch(() => null),
          ),
        ).then((results) => {
          if (!active) return;
          const next = {};
          for (const entry of results) {
            if (!entry) continue;
            const [code, live] = entry;
            const points = live?.data?.series?.[0]?.points;
            if (!points?.length) continue;
            // In-session the snapshot is still the previous trading day, so its
            // close is the correct 昨收 baseline.
            const baseline = payload.domestic_indices.find((item) => item.code === code)?.close;
            const livePrice = Number(points[points.length - 1].raw);
            if (!Number.isFinite(livePrice) || !Number.isFinite(Number(baseline)) || Number(baseline) === 0) continue;
            next[code] = {
              close: livePrice,
              pct_chg: (livePrice / Number(baseline) - 1) * 100,
              trade_date: "实时",
            };
          }
          setLiveDomesticIndices(next);
        });
      })
      .catch(() => {
        // Status unavailable: leave the published close showing rather than
        // risk labelling a stale number "实时".
      });
    return () => {
      active = false;
    };
  }, [payload]);

  React.useEffect(() => {
    if (!payload?.global_indices?.length) return undefined;
    let active = true;
    // East Money / Tencent carry live HK / Korea / US index quotes. The backend
    // returns a live quote (with as_of_ts) only while that market is actually
    // trading -- HK & Korea during the CN daytime, US indices overnight -- and
    // otherwise falls back to the EOD row (market_date, no as_of_ts). So a
    // present as_of_ts means "label it 实时"; its absence means show the close
    // date, and only overlay the EOD row when it is newer than the snapshot.
    Promise.all(
      payload.global_indices.map((item) =>
        getLatestGlobalIndex({ code: item.code })
          .then((latest) => [item.code, latest])
          .catch(() => null),
      ),
    ).then((results) => {
      if (!active) return;
      const next = {};
      for (const entry of results) {
        if (!entry) continue;
        const [code, latest] = entry;
        if (latest.as_of_ts) {
          next[code] = { close: latest.close, pct_chg: latest.pct_chg, trade_date: "实时" };
          continue;
        }
        const published = payload.global_indices.find((item) => item.code === code);
        if (!published || !latest.market_date || latest.market_date <= published.trade_date) continue;
        next[code] = { close: latest.close, pct_chg: latest.pct_chg, trade_date: latest.market_date };
      }
      setLatestGlobalIndices(next);
    });
    return () => {
      active = false;
    };
  }, [payload]);

  if (!payload && refreshing) return <MarketOverviewLoading />;
  if (!payload && error) return <ErrorState error={error} onRetry={load} />;
  if (!payload) return null;

  return (
    <div className={styles.page}>
      <PageHeading
        eyebrow="MARKET OVERVIEW"
        title="大盘概览"
        description="指数、市场宽度、成交与资金状态的一站式只读观察。"
        status={completeness === "provisional" && payload.status === "ok" ? "provisional" : payload.status}
        dataDate={payload.data_date}
        actions={
          <div className={styles.refreshControl}>
            {lastRefreshed && (
              <span className={styles.refreshTime}>
                更新于 {lastRefreshed.toLocaleTimeString("zh-CN", { hour12: false })}
              </span>
            )}
            <button
              type="button"
              className={styles.refreshButton}
              onClick={() => void refreshMarket()}
              disabled={refreshing}
            >
              <RefreshCw size={15} className={refreshing ? styles.spin : undefined} />
              {refreshing ? "刷新中" : "刷新"}
            </button>
          </div>
        }
      />

      <section className={styles.section}>
        <SectionTitle icon={<TrendingUp size={17} />} title="主要指数" meta="今日与近 5 个交易日" />
        <div className={styles.indexGrid}>
          {payload.domestic_indices.map((item) => <IndexCard key={item.code} item={{ ...item, ...liveDomesticIndices[item.code] }} styles={styles} onOpen={() => navigate(`/stock/${item.code}?from=${encodeURIComponent("/market")}`)} />)}
        </div>
      </section>

      <section className={styles.section}>
        <SectionTitle icon={<BarChart3 size={17} />} title="海外市场" meta="各市场最近可用交易日" />
        <div className={styles.indexGrid}>
          {payload.global_indices.map((item) => <IndexCard key={item.code} item={{ ...item, ...latestGlobalIndices[item.code] }} styles={styles} onOpen={() => navigate(`/stock/${item.code}?from=${encodeURIComponent("/market")}`)} />)}
        </div>
      </section>

      <section className={styles.card}>
        <SectionTitle icon={<Activity size={17} />} title="市场宽度" action={<span className={styles.metricTag}>数据日期 {shortDate(payload.data_date)}</span>} />
        {payload.breadth ? <BreadthPanel breadth={payload.breadth} tag={`${shortDate(payload.data_date)}收盘`} /> : <Unavailable reason="缺少沪深个股日线" />}
        <div className={styles.breadthChartsRow}>
          <div>
            <div className={styles.breadthSubHeader}><span>涨跌分布</span><span className={styles.metricTag}>截至{shortDate(payload.data_date)}收盘</span></div>
            {payload.turnover?.distribution ? (
              <div className={styles.distributionChart}><Chart option={distributionOption(payload.turnover.distribution)} /></div>
            ) : <Unavailable reason="没有可用涨跌分布" />}
          </div>
          <div>
            <div className={styles.breadthSubHeader}><span>近60日成交额（亿）</span><span className={styles.metricTag}>截至{shortDate(payload.data_date)}收盘</span></div>
            {payload.turnover?.series?.length ? (
              <div className={styles.mediumChart}><Chart option={lineOption(payload.turnover.series, "amount_yi", "成交额", "#2563eb", "亿元", "ma20_yi", "MA20")} /></div>
            ) : <Unavailable reason="缺少近 60 日成交额" />}
          </div>
        </div>
        {payload.breadth && payload.turnover && <BreadthSummaryLine breadth={payload.breadth} turnover={payload.turnover} />}
      </section>

      <section className={styles.twoColumn}>
        <article className={styles.card}>
          <MetricChartHeader
            title="融资余额"
            value={payload.margin?.balance_yi !== null && payload.margin?.balance_yi !== undefined ? `${number(payload.margin.balance_yi, 0)}亿` : "--"}
            metrics={[
              payload.margin?.change_yi !== null && payload.margin?.change_yi !== undefined
                ? { label: "日变", text: signed(payload.margin.change_yi, "亿", 0), tone: toneClass(payload.margin.change_yi, styles) }
                : null,
              payload.margin?.change_5d_yi !== null && payload.margin?.change_5d_yi !== undefined
                ? { label: "5日变", text: signed(payload.margin.change_5d_yi, "亿", 0), tone: toneClass(payload.margin.change_5d_yi, styles) }
                : null,
            ].filter(Boolean)}
            tag={payload.margin?.data_date ? `${shortDate(payload.margin.data_date)}收盘` : ""}
          />
          {payload.margin?.reason && <p className={styles.dataWarning}>{payload.margin.reason}</p>}
          {payload.margin?.series?.length ? <div className={styles.mediumChart}><Chart option={lineOption(payload.margin.series, "balance_yi", "融资余额", "#7c3aed", "亿元")} /></div> : <Unavailable reason="缺少融资余额数据" />}
        </article>
        <article className={styles.card}>
          <MetricChartHeader
            title={payload.connect?.label ?? "互联互通资金"}
            value={payload.connect ? signed(payload.connect.amount_yi, "亿", 1) : "--"}
            valueTone={payload.connect ? toneClass(payload.connect.amount_yi, styles) : undefined}
            metrics={payload.connect?.sum_5d_yi !== null && payload.connect?.sum_5d_yi !== undefined
              ? [{ label: "5日累计", text: signed(payload.connect.sum_5d_yi, "亿", 0), tone: toneClass(payload.connect.sum_5d_yi, styles) }]
              : []}
            tag={payload.connect?.data_date ? `${shortDate(payload.connect.data_date)}收盘` : ""}
          />
          {payload.connect?.series?.length ? <div className={styles.mediumChart}><Chart option={barOption(payload.connect.series, "amount_yi", payload.connect.series_label)} /></div> : <Unavailable reason="尚未同步互联互通数据" />}
        </article>
      </section>

      <HuijinEtfCard data={payload.huijin_etf} styles={styles} />

      <section className={styles.twoColumn}>
        <IndustrySummary type="performance" navigate={navigate} snapshotId={snapshotId} />
        <IndustrySummary type="flow" navigate={navigate} snapshotId={snapshotId} />
      </section>

      <EvidenceFooter payload={payload} />
    </div>
  );
}

function BreadthPanel({ breadth, tag }) {
  return (
    <div className={styles.breadthNumbersRow}>
      <div className={styles.up}><strong>{integer(breadth.rise)}</strong><span>上涨</span></div>
      <div className={styles.flat}><strong>{integer(breadth.flat)}</strong><span>平盘</span></div>
      <div className={styles.down}><strong>{integer(breadth.fall)}</strong><span>下跌</span></div>
      <div className={styles.up}><strong>{integer(breadth.limit_up)}</strong><span>涨停</span></div>
      <div className={styles.down}><strong>{integer(breadth.limit_down)}</strong><span>跌停</span></div>
      {tag && <span className={styles.metricTag}>{tag}</span>}
    </div>
  );
}

function BreadthSummaryLine({ breadth, turnover }) {
  const total = Number(breadth.total) || 1;
  const risePct = Math.round(Number(breadth.rise) / total * 100);
  const fallPct = Math.round(Number(breadth.fall) / total * 100);
  const changePct = turnover.change_pct === null || turnover.change_pct === undefined ? null : Number(turnover.change_pct);
  const volumeLabel = changePct === null ? null
    : Math.abs(changePct) < 3 ? "持平"
    : changePct > 0 ? `放量 +${changePct.toFixed(0)}%` : `缩量 ${changePct.toFixed(0)}%`;
  return (
    <div className={styles.breadthSummaryLine}>
      <span>涨跌比 <b className={styles.up}>{risePct}%</b> : <b className={styles.down}>{fallPct}%</b></span>
      <span>成交额 {number(turnover.amount_yi, 0)} 亿</span>
      {volumeLabel && <span className={styles.metricTag}>{volumeLabel}</span>}
      <span>MA20 {number(turnover.ma20_yi, 0)} 亿</span>
    </div>
  );
}

function MetricChartHeader({ title, value, valueTone, metrics = [], tag }) {
  return (
    <div className={styles.metricChartHeader}>
      <span className={styles.metricHeaderTitle}>{title}{tag && <span className={styles.metricTag}>{tag}</span>}</span>
      <div className={styles.metricHeaderValues}>
        <strong className={valueTone}>{value}</strong>
        {metrics.map((metric) => (
          <span key={metric.label}>{metric.label} <b className={metric.tone}>{metric.text}</b></span>
        ))}
      </div>
    </div>
  );
}

function IndustrySummary({ type, navigate, snapshotId, initialData = null }) {
  const [level, setLevel] = React.useState("L1");
  const [data, setData] = React.useState(initialData);
  const title = type === "performance" ? "行业涨跌" : "行业资金流";
  React.useEffect(() => {
    let active = true;
    if (type === "flow" && level === "L1" && initialData) {
      setData(initialData);
      return () => { active = false; };
    }
    const request = type === "performance"
      ? getMarketIndustries({ snapshotId, level, sort: "pct_chg", order: "desc" })
      : getMarketIndustryFlow({ snapshotId, level });
    request.then((payload) => { if (active) setData(payload); }).catch(() => { if (active) setData(null); });
    return () => { active = false; };
  }, [type, level, initialData, snapshotId]);
  let items = [];
  if (type === "performance" && data?.items) {
    items = data.items.filter((item) => item.industry_level === level);
    items = [...items].filter((item) => item.pct_chg !== null).sort((a, b) => Number(b.pct_chg) - Number(a.pct_chg));
  } else if (data?.items || data?.top) {
    items = [...(data.items ?? [...(data.top ?? []), ...(data.bottom ?? [])])]
      .filter((item) => item.main_net_inflow_yi !== null)
      .sort((a, b) => Number(b.main_net_inflow_yi) - Number(a.main_net_inflow_yi));
  }
  const top = items.slice(0, 8);
  const bottom = [...items].sort((a, b) => Number(type === "performance" ? a.pct_chg : a.main_net_inflow_yi) - Number(type === "performance" ? b.pct_chg : b.main_net_inflow_yi)).slice(0, 4);
  const omittedCount = Math.max(0, items.length - top.length - bottom.length);
  return (
    <article className={styles.card}>
      <div className={styles.industrySummaryHeader}>
        <div>
          {type === "performance" ? <TrendingUp size={17} /> : <BarChart3 size={17} />}
          <strong>{title}</strong>
          <span className={styles.metricTag}>{data?.data_date ?? "--"}</span>
        </div>
        <div className={styles.industrySummaryControls}>
          <div className={styles.segmented}>
            {[["L1", "一级"], ["L2", "二级"]].map(([value, label]) => (
              <button key={value} className={level === value ? styles.active : ""} onClick={() => setLevel(value)}>{label}</button>
            ))}
          </div>
          <button className={styles.linkButton} onClick={() => navigate(type === "performance" ? "/industry" : "/industry/flow")}>
            查看全部<ChevronRight size={15} />
          </button>
        </div>
      </div>
      {!data ? <Unavailable reason="正在读取行业数据" /> : !items.length ? (
        <Unavailable reason={level === "L2" ? "本地市场库尚未同步申万二级行业与成分关系" : type === "flow" ? "本地市场库尚未同步申万行业成分，不能把缺失资金流按 0 展示" : "本地市场库尚未同步申万行业指数或成分关系"} />
      ) : (
        <div className={styles.industrySummaryList}>
          {top.map((item, index) => renderSummaryRow(item, index, type, navigate))}
          {omittedCount > 0 && <div className={styles.industrySummaryOmitted}>中间 {omittedCount} 个已省略</div>}
          {bottom.map((item, index) => renderSummaryRow(item, `b${index}`, type, navigate))}
        </div>
      )}
    </article>
  );
}

function renderSummaryRow(item, key, type, navigate) {
  return (
    <button key={`${item.industry_code}-${key}`} onClick={() => navigate(`/industry/${item.industry_code}`)}>
      <strong>{item.industry_name}</strong>
      <span className={styles.industrySummaryValue}>
        {type === "performance" ? (
          <>
            <span className={styles.industryBreadthCounts}><span className={styles.up}>{integer(item.rise)}↑</span> / <span className={styles.down}>{integer(item.fall)}↓</span></span>
            <span className={toneClass(item.pct_chg, styles)}>{signed(item.pct_chg)}</span>
          </>
        ) : (
          <>
            <span>{number(item.turnover_yi, 1)} 亿</span>
            <span className={toneClass(item.main_net_inflow_yi, styles)}>{signed(item.main_net_inflow_yi, " 亿", 2)}</span>
          </>
        )}
      </span>
    </button>
  );
}
